import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { IEquipmentPrevOrder, IEquipmentPrevOrderStatus } from 'API';
import { CustomStepper } from 'Components/CustomStepper';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useState } from 'react';
import { EquipmentOrderStepper } from './EquipmentOrderStepper';
import { EquipmentStatusExtraInfo } from './EquipmentStatusExtraInfo';
import { ContactUs } from './ContactUs';
import { Status } from '../../EquipmentRecentOrders/Status';
import { shorterDateFormat } from 'Config';
import { format } from 'date-fns';
import {
  AccordionArrowPurple,
  AccordionArrowPurpleClose,
  PurpleCheckedBullet,
} from 'Images';

interface OrderStatusProps {
  order: IEquipmentPrevOrder;
}

export const EquipmentOrderStatus = ({ order }: OrderStatusProps) => {
  const isDesktop = useIsDesktop();
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Equipment);

  const currentStatus = order.statuses.find(
    (s) => s.mainStatus === order.currentStatus
  );

  const getStatusDate = () => {
    if (currentStatus?.mainStatusDate instanceof Date) {
      return format(currentStatus.mainStatusDate, shorterDateFormat);
    }
    return '';
  };

  return isDesktop ? (
    <>
      <Typography component={'h3'} mb={2}>
        {dictionary.orderDialog.status}
      </Typography>

      <Stack
        sx={{
          background: '#fff',
          border: '1px solid',
          borderColor: 'primary.shade_200',
          borderRadius: '16px',
          mb: 7,
          p: '24px 16px',
        }}
      >
        {order.currentStatus !== 8 ? (
          <Stack flexDirection={'row'}>
            <EquipmentOrderStepper
              steps={order.statuses}
              currentStep={order.currentStatus}
            />
            <Stack
              flexDirection={'column'}
              justifyContent={'start'}
              alignItems={'end'}
              p={3}
            >
              <EquipmentStatusExtraInfo
                stepStatus={currentStatus || ({} as IEquipmentPrevOrderStatus)}
              />
              <ContactUs order={order} />
            </Stack>
          </Stack>
        ) : (
          <Stack
            flexDirection={'column'}
            justifyContent={'start'}
            alignItems={'end'}
            p={3}
          >
            <EquipmentStatusExtraInfo
              stepStatus={currentStatus || ({} as IEquipmentPrevOrderStatus)}
            />
            <ContactUs order={order} />
          </Stack>
        )}
      </Stack>
    </>
  ) : (
    <>
      <Typography mb={2}>{dictionary.orderDialog.status}</Typography>
      <Accordion
        defaultExpanded
        sx={{
          width: '100%',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'primary.shade_200',
          borderRadius: '16px !important',
          '&:before': {
            display: 'none',
          },
          mb: '24px !important',
        }}
      >
        <AccordionSummary
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
          expandIcon={
            <>
              {!isExpanded && (
                <Avatar
                  src={AccordionArrowPurple}
                  sx={{
                    width: '32px',
                    height: '32px',
                  }}
                />
              )}
              {isExpanded && (
                <Avatar
                  src={AccordionArrowPurpleClose}
                  sx={{
                    width: '32px',
                    height: '32px',
                    rotate: '180deg',
                  }}
                />
              )}
            </>
          }
          sx={{ p: '10px 16px ' }}
        >
          {!isExpanded && (
            <Stack>
              <Stack flexDirection={'row'}>
                <Avatar
                  src={PurpleCheckedBullet}
                  sx={{ width: '24px', height: '24px', mr: '12px' }}
                  alt=''
                />
                <Status
                  status={order.currentStatus}
                  statusLabel={currentStatus?.mainStatusLabel || ''}
                />
              </Stack>

              <Typography variant={'body5'} sx={{ ml: '36px', mt: '4px' }}>
                {getStatusDate}
              </Typography>
            </Stack>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Grid
            sx={{
              '.MuiStepper-root > .MuiStepConnector-vertical:first-of-type': {
                display: 'none',
              },
            }}
          >
            <CustomStepper
              steps={order.statuses.map((s) => ({
                key: s.mainStatus,
                name: s.mainStatusLabel || '',
              }))}
              activeStepIndex={
                order.currentStatus === 1
                  ? order.currentStatus - 1
                  : order.currentStatus - 2
              }
              slideContent={
                <EquipmentStatusExtraInfo
                  stepStatus={
                    currentStatus || ({} as IEquipmentPrevOrderStatus)
                  }
                />
              }
            />
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
